import type { InitiatorTrustPayload } from "./InitiatorTrustBanner";

/** label — развёрнуто в попапе; labelShort — для плашки (узкая колонка, мобилка). */
export type CriterionLevel = "green" | "yellow" | "red";

export type InitiatorCriterionKind =
    | "registry"
    | "registry_empty"
    | "passport"
    | "selfie"
    | "activity"
    | "support";

export type InitiatorCriterion = {
    label: string;
    labelShort: string;
    ok: boolean;
    /** Явный уровень; если нет — green при ok, иначе yellow */
    level?: CriterionLevel;
    kind?: InitiatorCriterionKind;
};

/** Пороги из config.features.initiator_trust (API: initiator_trust_settings). */
export type InitiatorTrustSettings = {
    min_platform_days: number;
    min_documents_count: number;
    entrepreneur_fresh_low_max_platform_days: number;
    /** Показывать пункт «новый аккаунт / срок в сервисе / число документов». */
    show_new_account_criterion: boolean;
};

export const DEFAULT_INITIATOR_TRUST_SETTINGS: InitiatorTrustSettings = {
    min_platform_days: 30,
    min_documents_count: 20,
    entrepreneur_fresh_low_max_platform_days: 14,
    show_new_account_criterion: false,
};

function positiveInt(raw: unknown, fallback: number, minimum = 1): number {
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) {
        return fallback;
    }
    return Math.max(minimum, Math.trunc(n));
}

function nonNegativeInt(raw: unknown, fallback: number): number {
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) {
        return fallback;
    }
    return Math.max(0, Math.trunc(n));
}

function asBool(raw: unknown, fallback: boolean): boolean {
    if (raw === undefined || raw === null) {
        return fallback;
    }
    if (raw === true || raw === 1) {
        return true;
    }
    if (raw === false || raw === 0) {
        return false;
    }
    if (typeof raw === "string") {
        const s = raw.trim().toLowerCase();
        if (s === "true" || s === "1" || s === "yes" || s === "y" || s === "on") {
            return true;
        }
        if (s === "false" || s === "0" || s === "no" || s === "n" || s === "off") {
            return false;
        }
    }
    return fallback;
}

export function normalizeInitiatorTrustSettings(
    raw?: Partial<InitiatorTrustSettings> | null,
): InitiatorTrustSettings {
    if (!raw || typeof raw !== "object") {
        return DEFAULT_INITIATOR_TRUST_SETTINGS;
    }
    return {
        min_platform_days: positiveInt(raw.min_platform_days, 30),
        min_documents_count: positiveInt(raw.min_documents_count, 20),
        entrepreneur_fresh_low_max_platform_days: nonNegativeInt(
            raw.entrepreneur_fresh_low_max_platform_days,
            14,
        ),
        show_new_account_criterion: asBool(raw.show_new_account_criterion, false),
    };
}

export function criterionLevelOf(c: InitiatorCriterion): CriterionLevel {
    if (c.level) {
        return c.level;
    }
    return c.ok ? "green" : "yellow";
}

/**
 * Не менее min_documents_count выставленных документов.
 * Новый кэш: from_20. Старый: только high (20+); moderate = 5–19 — не проходит порог.
 */
function docsActivityOkWithLegacy(
    d: string | undefined,
    settings: InitiatorTrustSettings,
): boolean {
    return d === "from_20" || d === "high";
}

function platformDaysOk(
    days: number | null | undefined,
    settings: InitiatorTrustSettings,
): boolean {
    return (days ?? 0) >= settings.min_platform_days;
}

function formatIsoDateRu(iso: string | null | undefined): string | null {
    if (!iso || typeof iso !== "string" || iso.length < 10) return null;
    const [y, m, d] = iso.slice(0, 10).split("-");
    if (!y || !m || !d) return null;
    return `${d}.${m}.${y}`;
}

function daysWord(n: number): string {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return "дней";
    if (b === 1) return "день";
    if (b >= 2 && b <= 4) return "дня";
    return "дней";
}

/** Текст строки «срок в сервисе» с фактом, а не абстрактным требованием. */
function platformDaysCriterion(
    platformDays: number | null | undefined,
    platformSinceDate: string | null | undefined,
    settings: InitiatorTrustSettings,
): InitiatorCriterion {
    const d = platformDays ?? 0;
    const dateRu = formatIsoDateRu(platformSinceDate ?? undefined);
    const ok = platformDaysOk(platformDays, settings);

    if (dateRu) {
        const label = `Зарегистрирован в сервисе с ${dateRu} — прошло ${d} ${daysWord(d)}`
        const labelShort = `${d} дн. в сервисе ОкиДоки`
        return { label, labelShort, ok };
    }
    if (d > 0) {
        const label = `С даты регистрации в сервисе прошло ${d} ${daysWord(d)}`
        const labelShort = `${d} дн. в сервисе ОкиДоки`
        return { label, labelShort, ok };
    }
    return {
        label: "Дата регистрации в сервисе не указана — срок оценить нельзя",
        labelShort: "Срок в сервисе неизвестен",
        ok: false,
    };
}

function documentsActivityCriterion(
    documentsActivity: string | undefined,
    settings: InitiatorTrustSettings,
): InitiatorCriterion {
    const min = settings.min_documents_count;
    const ok = docsActivityOkWithLegacy(documentsActivity, settings);
    if (ok) {
        return {
            label: `В сервисе ОкиДоки выставлено ${min} и более документов`,
            labelShort: `${min}+ документов в ОкиДоки`,
            ok: true,
        };
    }
    return {
        label: `В сервисе ОкиДоки выставлено менее ${min} документов`,
        labelShort: `Менее ${min} документов в ОкиДоки`,
        ok: false,
    };
}

/** Оба порога антифрода: свежий аккаунт и мало документов именно в ОкиДоки. */
export function isNewOkiDokiAccount(
    platformDays: number | null | undefined,
    documentsActivity: string | undefined,
    settings?: Partial<InitiatorTrustSettings> | null,
): boolean {
    const cfg = normalizeInitiatorTrustSettings(settings);
    return (platformDays ?? 0) < cfg.min_platform_days
        && !docsActivityOkWithLegacy(documentsActivity, cfg);
}

/**
 * Для подписанта: одна жёлтая строка вместо отдельных «срок» и «число договоров».
 * null — не показывать ничего про активность.
 */
function newOkiDokiAccountCriterion(
    platformDays: number | null | undefined,
    documentsActivity: string | undefined,
    settings: InitiatorTrustSettings,
): InitiatorCriterion | null {
    if (!isNewOkiDokiAccount(platformDays, documentsActivity, settings)) {
        return null;
    }
    return {
        label:
            "Аккаунт недавно создан в сервисе ОкиДоки и пока использовался для небольшого числа документов. "
            + "Это не отражает объём вашего бизнеса вне сервиса.",
        labelShort: "Новый аккаунт в ОкиДоки",
        ok: false,
        level: "yellow",
    };
}

function ambiguousInnNeedsOgrnCriterion(): InitiatorCriterion {
    return {
        label:
            "По этому ИНН в реестре несколько организаций — для проверки укажите ОГРН (или ОГРНИП) в профиле",
        labelShort: "Укажите ОГРН",
        ok: false,
    };
}

/** Внутренние reason-коды (API/логи) → текст для пользователя. Коды в UI не показываем. */
const REGISTRY_DETAIL_USER_MESSAGES: Record<string, string> = {
    rusprofile_empty_or_blocked:
        "Не нашли организацию по указанному ОГРН в открытых источниках.",
    ogrn_invalid_for_rusprofile:
        "ОГРН указан в неверном формате — проверьте его в профиле.",
    rusprofile_page_timeout:
        "Временная ошибка при обращении к реестру. Попробуйте «Повторить проверку» позже.",
    rusprofile_fetch_error:
        "Не удалось получить данные из реестра. Проверьте ОГРН или попробуйте позже.",
    rusprofile_ai_timeout:
        "Автоматическая проверка временно недоступна. Попробуйте позже.",
    rusprofile_ai_error:
        "Автоматическая проверка временно недоступна. Попробуйте позже.",
    rusprofile_ai_parse_error:
        "Автоматическая проверка временно недоступна. Попробуйте позже.",
};

export const REGISTRY_SIGNER_NOTE =
    "Это справочные данные из открытого реестра, не оценка отправителя.";
const REGISTRY_OWNER_RETRY =
    "Исправьте ИНН, ОГРН и юридическое название в профиле и нажмите «Повторить проверку».";

function looksLikeMachineReasonCode(value: string): boolean {
    return /^[a-z][a-z0-9_]{2,}$/i.test(value) && !/\s/.test(value);
}

/** Человекочитаемый текст причины отказа реестра; сырые коды не отдаём. */
export function humanizeRegistryLookupDetail(detail?: string | null): string | null {
    const raw = (detail || "").trim();
    if (!raw) {
        return null;
    }
    if (REGISTRY_DETAIL_USER_MESSAGES[raw]) {
        return REGISTRY_DETAIL_USER_MESSAGES[raw];
    }
    if (looksLikeMachineReasonCode(raw)) {
        return "Не удалось подтвердить организацию по указанным реквизитам.";
    }
    return raw;
}

/**
 * Пояснение для попапа профиля при провале реестра (без внутренних кодов).
 * null — отдельный блок не нужен.
 */
export function getEntrepreneurRegistryFailureUserHint(
    trust: InitiatorTrustPayload,
): string | null {
    if (trust.status !== "ready" || trust.role !== "entrepreneur") {
        return null;
    }
    const e = trust.entrepreneur;
    const status = e?.registry_lookup_status;
    if (
        status !== "COUNTERPARTY_NOT_FOUND" &&
        status !== "NEEDS_MANUAL_VERIFICATION"
    ) {
        return null;
    }
    const human = humanizeRegistryLookupDetail(
        e?.registry_lookup_detail ?? e?.registry_lookup_message,
    );
    if (human) {
        return human;
    }
    if (status === "COUNTERPARTY_NOT_FOUND") {
        return "Не нашли организацию в реестре по указанным ИНН и ОГРН.";
    }
    return "Автоматическая проверка по реестру не удалась.";
}

export function missingRequisitesClientWarning(names: string): string {
    return `Без ${names} клиент увидит, что реквизиты не указаны.`;
}

export function getInitiatorTrustSendWarningCopy(trust: InitiatorTrustPayload): {
    title: string
    body: string
    reason: string
} {
    const inn = (trust.entrepreneur?.inn || "").trim()
    if (trust.role === "entrepreneur" && !inn) {
        return {
            title: "Не указан ИНН",
            body: "Клиент увидит предупреждение на странице подписания. Это не блокирует отправку: заполненный договор останется на месте.",
            reason: "Вы не указали ИНН — заполните в профиле.",
        }
    }
    const registryHint = getEntrepreneurRegistryFailureUserHint(trust)
    const failed = getInitiatorCriteria(trust, { viewerIsOwner: true }).filter((criterion) => !criterion.ok)
    const reason = registryHint || failed[0]?.label || "Автоматическая проверка реквизитов не подтвердилась."
    if (registryHint || failed.some((criterion) => criterion.kind === "registry")) {
        return {
            title: "Реквизиты пока не подтвердились в реестре",
            body: "Клиент увидит предупреждение на странице подписания. Это не блокирует отправку: заполненный договор останется на месте.",
            reason,
        }
    }
    return {
        title: "Клиенты увидят предупреждение при открытии договора",
        body: "Это не блокирует отправку: заполненный договор останется на месте. Проверки можно доделать в профиле позже.",
        reason,
    }
}

function registryFailureShortLabel(
    lookupStatus: string | null | undefined,
    lookupDetail?: string | null,
): string {
    if (lookupStatus === "AMBIGUOUS_INN_NEEDS_OGRN") {
        return "Укажите ОГРН";
    }
    const human = humanizeRegistryLookupDetail(lookupDetail) ?? "";
    if (/формат/i.test(human)) {
        return "ОГРН указан неверно";
    }
    if (/временно|недоступн/i.test(human)) {
        return "Реестр временно недоступен";
    }
    if (lookupStatus === "COUNTERPARTY_NOT_FOUND") {
        return "Не нашли в открытом реестре";
    }
    return "Реквизиты не подтвердились";
}

function registryVerificationFailedCriterion(
    lookupStatus: string | null | undefined,
    lookupDetail: string | null | undefined,
    forOwner: boolean,
): InitiatorCriterion | null {
    if (lookupStatus === "AMBIGUOUS_INN_NEEDS_OGRN") {
        return {
            ...ambiguousInnNeedsOgrnCriterion(),
            kind: "registry",
        };
    }
    if (!lookupStatus || lookupStatus === "OK") {
        return null;
    }

    const hint = forOwner ? REGISTRY_OWNER_RETRY : REGISTRY_SIGNER_NOTE;
    const humanDetail = humanizeRegistryLookupDetail(lookupDetail);
    const labelShort = registryFailureShortLabel(lookupStatus, lookupDetail);

    if (lookupStatus === "COUNTERPARTY_NOT_FOUND") {
        return {
            label: humanDetail
                ? `${humanDetail} ${hint}`
                : `Не нашли организацию в реестре по указанным ИНН и ОГРН. ${hint}`,
            labelShort,
            ok: false,
            kind: "registry",
        };
    }
    if (lookupStatus === "NEEDS_MANUAL_VERIFICATION") {
        return {
            label: humanDetail
                ? `${humanDetail} ${hint}`
                : `Автоматическая проверка по реестру не удалась. ${hint}`,
            labelShort,
            ok: false,
            kind: "registry",
        };
    }

    return {
        label: humanDetail ? `${humanDetail} ${hint}` : hint,
        labelShort,
        ok: false,
        kind: "registry",
    };
}

function entrepreneurRegistryErrorCriterion(
    lookupStatus: string | null | undefined,
    lookupDetail: string | null | undefined,
    forOwner: boolean,
): InitiatorCriterion | null {
    return registryVerificationFailedCriterion(lookupStatus, lookupDetail, forOwner);
}

function egrRegistrationCriterion(
    egrRegistrationDate: string | null | undefined,
    registryManualVerified?: boolean,
    inn?: string | null,
): InitiatorCriterion {
    if (registryManualVerified) {
        return {
            label: "Реквизиты организации подтверждены вручную поддержкой",
            labelShort: "Подтверждено вручную",
            ok: true,
            kind: "registry",
        };
    }
    if (!(inn || "").trim()) {
        return {
            label: "Вы не указали ИНН — заполните в профиле.",
            labelShort: "Не указан ИНН",
            ok: false,
            kind: "registry",
        };
    }
    const raw = egrRegistrationDate != null ? String(egrRegistrationDate).trim() : "";
    const ok = raw.length >= 8;
    if (!ok) {
        return {
            label: "По ИНН в открытых данных нет даты регистрации в ЕГРЮЛ/ЕГРИП",
            labelShort: "Нет даты в ЕГРЮЛ",
            ok: false,
            kind: "registry",
        };
    }
    const dateRu = formatIsoDateRu(raw) ?? raw;
    return {
        label: `В ЕГРЮЛ/ЕГРИП по ИНН указана дата регистрации: ${dateRu}`,
        labelShort: `Дата регистрации юр. лица: ${dateRu}`,
        ok: true,
        kind: "registry",
    };
}

function businessActiveCriterion(
    businessActive: boolean | null | undefined,
    registryManualVerified?: boolean,
): InitiatorCriterion {
    if (registryManualVerified) {
        return {
            label: "Статус организации подтверждён вручную поддержкой",
            labelShort: "Статус подтверждён",
            ok: true,
            kind: "registry",
        };
    }
    if (businessActive === true) {
        return {
            label: "По данным ЕГРЮЛ/ЕГРИП статус организации: действует",
            labelShort: "По данным реестра: действует",
            ok: true,
            kind: "registry",
        };
    }
    if (businessActive === false) {
        return {
            label: "По данным ЕГРЮЛ/ЕГРИП статус организации: не действует",
            labelShort: "По данным реестра: не действует",
            ok: false,
            kind: "registry",
        };
    }
    return {
        label: "Статус в реестре не получен (данные недоступны или контрагент не найден)",
        labelShort: "По данным реестра: нет данных",
        ok: false,
        kind: "registry",
    };
}

/** Подпись и галочка по значению passport_check из API (как в verify_passport). */
export function passportCriterionFromStatus(passportCheck: string | undefined): InitiatorCriterion {
    switch (passportCheck) {
        case "VALID":
            return {
                label: "Паспорт РФ: по базе действителен",
                labelShort: "Паспорт действителен",
                ok: true,
            };
        case "NOT_VALID":
            return {
                label: "Паспорт РФ: в базе найден, недействителен",
                labelShort: "Паспорт недействителен",
                ok: false,
            };
        case "NOT_FOUND":
            return {
                label: "Паспорт РФ: в базе не найден",
                labelShort: "Паспорт не в базе",
                ok: false,
            };
        case "ERROR":
            return {
                label: "Паспорт РФ: ошибка при проверке",
                labelShort: "Ошибка проверки паспорта",
                ok: false,
            };
        case "NON_RUSSIAN_PASSPORT":
            return {
                label: "Паспорт: указан документ другой страны, проверка по базе РФ не выполнялась",
                labelShort: "Документ не РФ",
                ok: true,
            };
        case "NOT_PROVIDED":
            return {
                label: "Паспорт РФ: данные для проверки не указаны",
                labelShort: "Паспорт не указан",
                ok: false,
            };
        default:
            return {
                label: passportCheck
                    ? `Паспорт РФ: нестандартный статус (${passportCheck})`
                    : "Паспорт РФ: проверка не выполнялась",
                labelShort: passportCheck ? `Паспорт: ${passportCheck}` : "Паспорт не проверен",
                ok: false,
            };
    }
}

/** Подпись и галочка по значению selfie_check из API (Beorg match_faces). */
export function selfieCriterionFromStatus(selfieCheck: string | undefined): InitiatorCriterion {
    switch (selfieCheck) {
        case "PASSED":
            return {
                label: "Селфи с паспортом: лицо на фото совпадает",
                labelShort: "Селфи подтверждено",
                ok: true,
            };
        case "PENDING":
            return {
                label: "Селфи с паспортом: проверка выполняется, обычно до нескольких минут",
                labelShort: "Селфи проверяется…",
                ok: false,
            };
        case "FAILED":
            return {
                label: "Селфи с паспортом: лицо на фото не совпадает",
                labelShort: "Селфи не совпало",
                ok: false,
            };
        case "ERROR":
            return {
                label: "Селфи с паспортом: ошибка при проверке",
                labelShort: "Ошибка проверки селфи",
                ok: false,
            };
        case "NOT_PROVIDED":
            return {
                label: "Селфи с паспортом: фото не загружены",
                labelShort: "Селфи не загружено",
                ok: false,
            };
        case "NOT_VERIFIED":
            return {
                label: "Селфи с паспортом: разовая проверка не пройдена",
                labelShort: "Селфи не проверено",
                ok: false,
            };
        case "NON_RUSSIAN_PASSPORT":
            return {
                label: "Селфи с паспортом: для этого типа документа автопроверка не требуется",
                labelShort: "Селфи: не РФ-паспорт",
                ok: true,
            };
        default:
            return {
                label: selfieCheck
                    ? `Селфи с паспортом: нестандартный статус (${selfieCheck})`
                    : "Селфи с паспортом: проверка не выполнялась",
                labelShort: selfieCheck ? `Селфи: ${selfieCheck}` : "Селфи не проверено",
                ok: false,
            };
    }
}

function shouldShowSelfieCriterion(
    selfieCheck: string | undefined,
    selfieDisplayMode: string | undefined,
    viewerIsOwner?: boolean,
): boolean {
    const mode = selfieDisplayMode ?? "hidden";
    if (mode === "hidden") {
        return false;
    }
    // Владелец профиля видит свой статус (в т.ч. FAILED/PENDING), иначе при trusted_only
    // провал проверки незаметен — «лоадер пропал и ничего не произошло».
    if (viewerIsOwner && (mode === "all" || mode === "trusted_only")) {
        return true;
    }
    if (!selfieCheck) {
        return mode === "all";
    }
    if (mode === "trusted_only") {
        return selfieCheck === "PASSED";
    }
    return mode === "all";
}

export function needsInitiatorSelfieVerification(
    trust: InitiatorTrustPayload | null | undefined,
    selfieDisplayMode?: string,
): boolean {
    const mode = selfieDisplayMode ?? "hidden";
    if (mode === "hidden") {
        return false;
    }
    if (!trust || trust.status !== "ready" || trust.role !== "customer") {
        return false;
    }
    const selfieCheck = trust.customer?.selfie_check;
    if (
        selfieCheck === "PASSED"
        || selfieCheck === "PENDING"
    ) {
        return false;
    }
    return true;
}

export type InitiatorCriteriaOptions = {
    selfieDisplayMode?: string;
    /** true — критерии для владельца аккаунта в профиле (показываем FAILED/PENDING) */
    viewerIsOwner?: boolean;
    /** Пороги из API (initiator_trust_settings); по умолчанию 30 дней / 20 документов */
    trustSettings?: Partial<InitiatorTrustSettings> | null;
};

export function getInitiatorCriteria(
    trust: InitiatorTrustPayload,
    options?: InitiatorCriteriaOptions,
): InitiatorCriterion[] {
    if (trust.status !== "ready") {
        return [];
    }

    const trustSettings = normalizeInitiatorTrustSettings(options?.trustSettings);
    const hideActivity = !!trust.hide_activity_criteria;
    const items: InitiatorCriterion[] = [];

    const supportNote = supportNoteCriterion(trust.support_note);
    if (supportNote) {
        items.push(supportNote);
    }

    const appendActivityCriteria = (branch: {
        platform_days?: number | null;
        platform_since_date?: string | null;
        documents_activity?: string;
    } | null | undefined, docsFallback?: string) => {
        if (!trustSettings.show_new_account_criterion) {
            return;
        }
        const platformDays = branch?.platform_days;
        const documentsActivity = branch?.documents_activity ?? docsFallback;

        if (options?.viewerIsOwner) {
            const pushIfVisible = (criterion: InitiatorCriterion) => {
                // hide_activity_criteria — только предупреждения; пройденные проверки показываем
                if (!hideActivity || criterion.ok) {
                    items.push(criterion);
                }
            };
            pushIfVisible(
                platformDaysCriterion(
                    platformDays,
                    branch?.platform_since_date ?? null,
                    trustSettings,
                ),
            );
            pushIfVisible(documentsActivityCriterion(documentsActivity, trustSettings));
            return;
        }

        const combined = newOkiDokiAccountCriterion(platformDays, documentsActivity, trustSettings);
        if (combined && !hideActivity) {
            items.push(combined);
        }
    };

    if (trust.role === "entrepreneur") {
        const e = trust.entrepreneur;
        const docs = e?.documents_activity ?? trust.documents_activity;
        const manual = !!e?.registry_manual_verified;
        const inn = (e?.inn || "").trim();
        if (!manual && !inn) {
            items.push({
                label: options?.viewerIsOwner
                    ? "Вы не указали ИНН — заполните в профиле."
                    : "Реквизиты не указаны.",
                labelShort: options?.viewerIsOwner ? "Не указан ИНН" : "Реквизиты не указаны",
                ok: false,
                kind: "registry_empty",
            });
            appendActivityCriteria(e, docs);
            return items;
        }
        const registryError = !manual
            ? entrepreneurRegistryErrorCriterion(
                e?.registry_lookup_status,
                e?.registry_lookup_detail ?? e?.registry_lookup_message,
                !!options?.viewerIsOwner,
            )
            : null;
        if (registryError) {
            items.push(registryError);
            appendActivityCriteria(e, docs);
            return items;
        }
        items.push(
            egrRegistrationCriterion(e?.egr_registration_date, manual, inn),
            businessActiveCriterion(e?.business_active, manual),
        );
        appendActivityCriteria(e, docs);
        return items;
    }

    if (trust.role === "customer") {
        const c = trust.customer;
        const docs = c?.documents_activity ?? trust.documents_activity;
        items.push(passportCriterionFromStatus(c?.passport_check));
        if (shouldShowSelfieCriterion(
            c?.selfie_check,
            options?.selfieDisplayMode,
            options?.viewerIsOwner,
        )) {
            items.push(selfieCriterionFromStatus(c?.selfie_check ?? "NOT_VERIFIED"));
        }
        appendActivityCriteria(c, docs);
        return items;
    }

    return items;
}

function supportNoteCriterion(
    note: InitiatorTrustPayload["support_note"],
): InitiatorCriterion | null {
    if (!note || typeof note !== "object") {
        return null;
    }
    const levelRaw = String(note.level || "").trim().toLowerCase();
    if (levelRaw !== "green" && levelRaw !== "yellow" && levelRaw !== "red") {
        return null;
    }
    const title = (note.title || "").trim();
    if (!title) {
        return null;
    }
    const text = (note.text || "").trim();
    return {
        label: text || title,
        labelShort: title,
        ok: levelRaw === "green",
        level: levelRaw,
        kind: "support",
    };
}

export type InitiatorTrustFixAction = {
    id: "edit_profile" | "refresh" | "retry_registry_check";
    label: string;
};

function entrepreneurRegistryChecksFailed(trust: InitiatorTrustPayload): boolean {
    if (trust.status !== "ready" || trust.role !== "entrepreneur") {
        return false;
    }
    const e = trust.entrepreneur;
    if (e?.registry_manual_verified) {
        return false;
    }
    if (
        e?.registry_lookup_status === "AMBIGUOUS_INN_NEEDS_OGRN" ||
        e?.registry_lookup_status === "COUNTERPARTY_NOT_FOUND" ||
        e?.registry_lookup_status === "NEEDS_MANUAL_VERIFICATION"
    ) {
        return true;
    }
    const criteria = getInitiatorCriteria(trust);
    return criteria.some((c) => !c.ok && c.kind === "registry");
}

export function needsEntrepreneurRegistryRetry(
    trust: InitiatorTrustPayload | null | undefined,
): boolean {
    if (!trust || trust.role !== "entrepreneur") {
        return false;
    }
    if (trust.status === "pending") {
        return false;
    }
    return entrepreneurRegistryChecksFailed(trust);
}

/**
 * Действия, которые инициатор может предпринять в профиле, чтобы улучшить проверку.
 */
export function getInitiatorTrustFixActions(trust: InitiatorTrustPayload): InitiatorTrustFixAction[] {
    if (trust.status === "pending") {
        return [{ id: "refresh", label: "Обновить статус" }];
    }
    if (trust.status !== "ready") {
        return [];
    }

    const actions: InitiatorTrustFixAction[] = [];

    if (trust.role === "customer") {
        const passportCheck = trust.customer?.passport_check;
        if (
            passportCheck === "NOT_PROVIDED"
            || passportCheck === "NOT_VALID"
            || passportCheck === "NOT_FOUND"
            || passportCheck === "ERROR"
        ) {
            actions.push({
                id: "edit_profile",
                label:
                    passportCheck === "NOT_PROVIDED"
                        ? "Указать паспортные данные"
                        : "Исправить паспортные данные",
            });
        }
    }

    if (trust.role === "entrepreneur") {
        const e = trust.entrepreneur;
        if (!e?.inn) {
            actions.push({
                id: "edit_profile",
                label: "Указать ИНН и реквизиты",
            });
        } else if (e.registry_lookup_status === "AMBIGUOUS_INN_NEEDS_OGRN") {
            actions.push({
                id: "edit_profile",
                label: "Указать ОГРН в профиле",
            });
        } else if (
            e.registry_lookup_status === "COUNTERPARTY_NOT_FOUND" ||
            e.registry_lookup_status === "NEEDS_MANUAL_VERIFICATION"
        ) {
            actions.push({
                id: "edit_profile",
                label: "Проверить ИНН и ОГРН в профиле",
            });
            actions.push({
                id: "retry_registry_check",
                label: "Повторить проверку",
            });
        } else if (entrepreneurRegistryChecksFailed(trust)) {
            actions.push({
                id: "retry_registry_check",
                label: "Повторить проверку",
            });
            actions.push({
                id: "edit_profile",
                label: "Проверить ИНН и реквизиты",
            });
        }
    }

    return actions;
}
